import { Check, Clock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { errorMessage } from '@/api/client';
import { useConnections, useIntegrations } from '@/api/hooks';
import { useAuth } from '@/auth/context';
import { Logo } from '@/components/Logo';
import { Button, ButtonLink, Card, ErrorNote, IntegrationIcon, LiveRegion, Skeleton } from '@/components/ui';
import { ConnectPanel } from '@/features/connect/ConnectPanel';
import { RequestIntegrationDialog } from '@/features/integrations/RequestDialogs';
import { cx } from '@/lib/cx';
import { currentOnboardingStep, readOnboarding, writeOnboarding, type OnboardingState } from './state';

/** After sign-up: "Which apps do you use?" → connect them one by one. */
export function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const integrations = useIntegrations();
  const connections = useConnections();
  const [state, setState] = useState<OnboardingState | null>(readOnboarding);
  const [picked, setPicked] = useState<string[]>([]);
  const location = useLocation();
  // Connecting marks steps done in sessionStorage and navigates back here: re-read it.
  useEffect(() => setState(readOnboarding()), [location.key]);
  const [requestOpen, setRequestOpen] = useState(false);

  const update = (s: OnboardingState | null) => {
    writeOnboarding(s);
    setState(s);
  };

  const groups = useMemo(() => {
    const d = integrations.data;
    if (!d) return [];
    return d.categories.map((c) => ({ category: c, items: d.integrations.filter((i) => i.category === c.id) })).filter((g) => g.items.length);
  }, [integrations.data]);

  const stepId = currentOnboardingStep(state);
  const current = integrations.data?.integrations.find((i) => i.id === stepId);
  const total = state?.queue.length ?? 0;
  const index = state ? state.queue.indexOf(stepId ?? '') + 1 : 0;

  const finish = () => {
    update(null);
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-dvh bg-paper">
      <header className="flex items-center justify-between px-4 py-5 sm:px-8">
        <Logo className="h-8" />
        <Link to="/" onClick={() => writeOnboarding(null)} className="text-sm text-muted-ink hover:text-ink">
          Skip for now
        </Link>
      </header>
      <main className="mx-auto max-w-4xl px-4 pb-16 sm:px-8">
        {integrations.isError && <ErrorNote message={errorMessage(integrations.error)} onRetry={() => void integrations.refetch()} />}

        {!state && (
          <>
            <h1 className="mt-6 text-[28px] font-bold tracking-[-0.03em] sm:text-4xl">
              {user ? `Welcome, ${user.name.split(' ')[0]}.` : 'Welcome.'} Which apps do you use?
            </h1>
            <p className="mt-2 text-muted-ink">Pick everything that applies. We’ll connect them one at a time, and each brings its headline numbers.</p>
            <LiveRegion message={picked.length ? `${picked.length} selected` : ''} />
            {integrations.isPending && <Skeleton className="mt-8 h-96" />}
            <div className="mt-8 flex flex-col gap-8">
              {groups.map(({ category, items }) => (
                <fieldset key={category.id}>
                  <legend className="mb-3 text-sm font-semibold tracking-wide text-muted-ink uppercase">{category.name}</legend>
                  <div className="grid grid-cols-1 gap-2 min-[440px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {items.map((i) => {
                      const on = picked.includes(i.id);
                      const soon = i.status === 'partner_required';
                      const already = connections.data?.some((c) => c.integration_id === i.id);
                      return (
                        <button
                          key={i.id}
                          type="button"
                          aria-pressed={on}
                          onClick={() => setPicked((p) => (on ? p.filter((x) => x !== i.id) : [...p, i.id]))}
                          className={cx(
                            'relative flex items-center gap-3 rounded-card border p-3 text-left transition-colors',
                            on ? 'border-ink bg-card shadow-card' : 'border-border bg-card hover:bg-muted',
                          )}
                        >
                          <IntegrationIcon name={i.name} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{i.name}</span>
                            <span className="block truncate text-xs text-muted-ink">
                              {soon ? 'Coming soon' : already ? 'Connected' : i.status === 'beta' ? 'Beta' : i.status === 'byo_credentials' ? 'Your own keys' : i.account_label}
                            </span>
                          </span>
                          <span
                            aria-hidden
                            className={cx('flex size-5 shrink-0 items-center justify-center rounded-full border', on ? 'border-ink bg-ink text-paper' : 'border-border')}
                          >
                            {on && <Check className="size-3" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
            </div>
            <div className="sticky bottom-0 mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-paper/95 py-4 backdrop-blur">
              <button type="button" onClick={() => setRequestOpen(true)} className="text-sm text-muted-ink underline-offset-4 hover:text-ink hover:underline">
                Don’t see your app? Request it
              </button>
              <Button variant="primary" size="lg" disabled={!picked.length} onClick={() => update({ queue: picked, done: [], skipped: [] })}>
                {picked.length ? `Connect ${picked.length} app${picked.length === 1 ? '' : 's'}` : 'Pick at least one app'}
              </Button>
            </div>
          </>
        )}

        {state && current && (
          <div className="mx-auto mt-6 max-w-xl">
            <p className="text-sm text-muted-ink" aria-live="polite">
              App {index} of {total}
            </p>
            <div className="mt-2 flex gap-1" aria-hidden>
              {state.queue.map((id) => (
                <span
                  key={id}
                  className={cx('h-1.5 flex-1 rounded-full', state.done.includes(id) ? 'bg-ink' : id === stepId ? 'bg-yellow' : 'bg-muted')}
                />
              ))}
            </div>
            <Card className="mt-6 p-6">
              <div className="flex items-center gap-3">
                <IntegrationIcon name={current.name} size="lg" />
                <div>
                  <h1 className="text-xl font-semibold">Connect {current.name}</h1>
                  <p className="text-sm text-muted-ink">{current.kpis.map((k) => k.label).slice(0, 5).join(' · ') || 'Prices for your ticker symbols'}</p>
                </div>
              </div>
              <div className="mt-5">
                <ConnectPanel key={current.id} integration={current} next="/onboarding" />
              </div>
            </Card>
            <div className="mt-4 flex justify-between">
              <Button variant="ghost" onClick={() => update({ ...state, skipped: [...state.skipped, current.id] })}>
                Skip {current.name}
              </Button>
              {current.status === 'partner_required' && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-ink">
                  <Clock className="size-3.5" aria-hidden /> Coming soon
                </span>
              )}
            </div>
          </div>
        )}

        {state && !stepId && (
          <div className="mx-auto mt-16 max-w-md text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-yellow text-yellow-ink">
              <Check className="size-6" aria-hidden />
            </span>
            <h1 className="mt-4 text-2xl font-semibold">You’re set</h1>
            <p className="mt-2 text-sm text-muted-ink">
              {state.done.length
                ? `${state.done.length} app${state.done.length === 1 ? '' : 's'} connected. Your numbers appear after the first sync, usually within a minute.`
                : 'Nothing connected yet. You can connect apps any time from Integrations.'}
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button variant="primary" onClick={finish}>
                Go to my dashboard
              </Button>
              <ButtonLink to="/integrations" onClick={() => writeOnboarding(null)}>
                Browse integrations
              </ButtonLink>
            </div>
          </div>
        )}
      </main>
      <RequestIntegrationDialog open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
